/**
 * Constantes pour les types d'animaux utilisés dans l'application.
 * Ce fichier centralise toutes les définitions liées aux types d'animaux.
 */

/**
 * Interface pour un type d'animal avec ses métadonnées.
 */
export interface AnimalType {
    id: number;
    label: string;
}

/**
 * Interface pour les icônes d'un type d'animal (état actif/inactif/survol).
 */
export interface AnimalTypeIcons {
    on: string;
    off: string;
    hover: string;
}

/**
 * Liste complète des types d'animaux supportés par l'application.
 */
export const ANIMAL_TYPES: readonly AnimalType[] = [
    { id: 0, label: 'Bovin' },
    { id: 1, label: 'Caprin' },
    { id: 2, label: 'Ovin' },
    { id: 3, label: 'Equin' }
] as const;

/**
 * IDs des types d'animaux disponibles.
 */
export const ANIMAL_TYPE_IDS: readonly number[] = [0, 1, 2, 3] as const;

/**
 * Map des labels de types d'animaux par ID.
 */
export const ANIMAL_TYPE_LABELS: Readonly<Record<number, string>> = {
    0: 'Bovin',
    1: 'Caprin',
    2: 'Ovin',
    3: 'Equin'
} as const;

/**
 * Map des icônes de types d'animaux par ID (état actif/inactif/survol).
 * Utilisé pour l'affichage du sélecteur de type dans la navigation.
 */
export const ANIMAL_TYPE_ICONS: Readonly<Record<number, AnimalTypeIcons>> = {
    0: { on: 'q-pltf-bovin-on', off: 'q-pltf-bovin-off', hover: 'q-pltf-bovin-hover' },
    1: { on: 'q-pltf-caprin-on', off: 'q-pltf-caprin-off', hover: 'q-pltf-caprin-hover' },
    2: { on: 'q-pltf-ovin-on', off: 'q-pltf-ovin-off', hover: 'q-pltf-ovin-hover' },
    3: { on: 'q-pltf-equin-on', off: 'q-pltf-equin-off', hover: 'q-pltf-equin-hover' }
} as const;

/**
 * Map des icônes d'élevage par type d'animal.
 * Utilisé pour afficher l'icône correspondant au type d'élevage en cours.
 */
export const ANIMAL_TYPE_BREEDING_ICONS: Readonly<Record<number, string>> = {
    0: 'q-elevage-bovin',
    1: 'q-elevage-caprin',
    2: 'q-elevage-ovin',
    3: 'q-elevage-equin'
} as const;

/**
 * Map des icônes d'ajout de lot par type d'animal.
 * Utilisé pour afficher l'icône d'ajout de lot correspondant au type d'animal en cours.
 */
export const ANIMAL_TYPE_ADD_BATCH_ICONS: Readonly<Record<number, string>> = {
    0: 'q-ajout-lot-bovin',
    1: 'q-ajout-lot-caprin',
    2: 'q-ajout-lot-ovin',
    3: 'q-ajout-lot-equin'
} as const;

/**
 * Plafond du filtre de poids (en kg) par type d'animal.
 * Utilisé pour adapter la plage du slider de poids selon l'espèce.
 */
export const ANIMAL_TYPE_WEIGHT_CEIL: Readonly<Record<number, number>> = {
    0: 1000, // Bovin
    1: 100,  // Caprin
    2: 150,  // Ovin
    3: 1000  // Equin
} as const;

/**
 * Plafond de poids par défaut lorsque le type d'animal est inconnu.
 */
export const DEFAULT_WEIGHT_CEIL = 1000;

/**
 * Helper pour obtenir le plafond du filtre de poids correspondant au type d'animal.
 * @param typeId L'ID du type d'animal (peut être null)
 * @returns Le plafond de poids en kg, ou la valeur par défaut
 */
export function getAnimalTypeWeightCeil(typeId: number | null): number {
    if (typeId === null || typeId === undefined) {
        return DEFAULT_WEIGHT_CEIL;
    }
    return ANIMAL_TYPE_WEIGHT_CEIL[typeId] ?? DEFAULT_WEIGHT_CEIL;
}

/**
 * Helper pour obtenir le label d'un type d'animal.
 * @param typeId L'ID du type d'animal
 * @returns Le label du type ou un fallback
 */
export function getAnimalTypeLabel(typeId: number): string {
    return ANIMAL_TYPE_LABELS[typeId] ?? `Type ${typeId}`;
}

/**
 * Helper pour obtenir l'icône d'élevage correspondant au type d'animal.
 * @param typeId L'ID du type d'animal (peut être null)
 * @returns L'icône d'élevage correspondante, ou l'icône bovin par défaut
 */
export function getAnimalTypeBreedingIcon(typeId: number | null): string {
    if (typeId === null || typeId === undefined) {
        return 'q-elevage-bovin'; // icône par défaut
    }
    return ANIMAL_TYPE_BREEDING_ICONS[typeId] ?? 'q-elevage-bovin';
}

/**
 * Helper pour obtenir l'icône d'ajout de lot correspondant au type d'animal.
 * @param typeId L'ID du type d'animal (peut être null)
 * @returns L'icône d'ajout de lot correspondante, ou l'icône bovin par défaut
 */
export function getAnimalTypeAddBatchIcon(typeId: number | null): string {
    if (typeId === null || typeId === undefined) {
        return 'q-ajout-lot-bovin'; // icône par défaut
    }
    return ANIMAL_TYPE_ADD_BATCH_ICONS[typeId] ?? 'q-ajout-lot-bovin';
}

/**
 * Helper pour obtenir les labels des types d'animaux séparés par des virgules.
 * @param animalTypeIds Tableau des IDs de types d'animaux
 * @returns Les labels séparés par des virgules, ou "-" si vide
 */
export function getAnimalTypesLabel(animalTypeIds: number[] | undefined): string {
    if (!animalTypeIds || animalTypeIds.length === 0) {
        return "-";
    }
    return animalTypeIds.map((typeId) => getAnimalTypeLabel(typeId)).join(", ");
}

// Races "spéciales" temporaires : l'espèce d'un animal est déterminée par sa
// race (cf. resolveAnimalType côté API).
export const CAPRIN_BREED_ID = 10001;
export const OVIN_BREED_ID = 20001;
export const EQUIN_BREED_ID = 30001;
